import { Dimensions } from 'react-native';
import {
  MARKETPLACE_MAX_CONTENT_WIDTH as MAX_CONTENT_WIDTH,
  MARKETPLACE_GRID_COLUMNS,
  MARKETPLACE_GRID_GAP,
  MARKETPLACE_CONTENT_PADDING,
  MARKETPLACE_FRAME_PADDING,
  CAROUSEL_DIMENSIONS,
} from '@/constants/residents/marketplaceConstants';

type LayoutMode = 'mobile' | 'tablet' | 'desktop';

interface GridSizeOptions {
  layoutMode: LayoutMode;
  numColumns?: number;
  gridGap?: number;
  gridPadding?: number;
  maxContentWidth?: number;
}

// Legacy interface for backwards compatibility
interface LegacyGridSizeOptions {
  isMobile: boolean;
  numColumns?: number;
  gridGap?: number;
  gridPadding?: number;
}

// Re-export for backwards compatibility
export const MARKETPLACE_MAX_CONTENT_WIDTH = MAX_CONTENT_WIDTH;

/**
 * Get the number of columns based on layout mode
 */
export const getResponsiveColumns = (layoutMode: LayoutMode): number => {
  return MARKETPLACE_GRID_COLUMNS[layoutMode] ?? MARKETPLACE_GRID_COLUMNS.mobile;
};

/**
 * Get responsive grid gap based on layout mode
 */
export const getResponsiveGridGap = (layoutMode: LayoutMode): number => {
  return MARKETPLACE_GRID_GAP[layoutMode] ?? MARKETPLACE_GRID_GAP.mobile;
};

/**
 * Get responsive padding based on layout mode
 */
export const getResponsivePadding = (layoutMode: LayoutMode): number => {
  return MARKETPLACE_CONTENT_PADDING[layoutMode] ?? MARKETPLACE_CONTENT_PADDING.mobile;
};

/**
 * Calculate the size of grid items for marketplace listings
 * Accounts for frame padding, content padding, and gaps between items
 * 
 * @param options - Grid configuration options
 * @returns The calculated item size in pixels
 */
export const calculateGridItemSize = (
  options: GridSizeOptions | LegacyGridSizeOptions
): number => {
  const screenWidth = Dimensions.get('window').width;
  
  // Handle legacy isMobile option
  if ('isMobile' in options) {
    const { isMobile, numColumns = 2, gridGap = 15, gridPadding = 20 } = options;
    const framePadding = isMobile ? (3 + 2 + 1) * 2 : 0;
    const availableWidth = screenWidth - framePadding - (gridPadding * 2) - (gridGap * (numColumns - 1));
    return Math.floor(availableWidth / numColumns);
  }
  
  // New responsive options
  const { layoutMode, numColumns, gridGap, gridPadding, maxContentWidth } = options;
  
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';
  
  // Calculate actual values based on layout mode
  const actualColumns = numColumns ?? getResponsiveColumns(layoutMode);
  const actualGap = gridGap ?? getResponsiveGridGap(layoutMode);
  const actualPadding = gridPadding ?? getResponsivePadding(layoutMode);
  
  // Frame padding only applies to mobile layout
  const framePadding = isMobile ? (3 + 2 + 1) * 2 : 0;
  
  // Calculate the content width (respecting max width constraints on larger screens)
  let contentWidth = screenWidth - framePadding;
  
  if (isTablet || isDesktop) {
    const maxWidth = maxContentWidth ?? MARKETPLACE_MAX_CONTENT_WIDTH[layoutMode as 'tablet' | 'desktop'];
    contentWidth = Math.min(contentWidth, maxWidth);
  }
  
  const availableWidth = contentWidth - (actualPadding * 2) - (actualGap * (actualColumns - 1));
  const itemSize = Math.floor(availableWidth / actualColumns);
  
  return itemSize;
};

/**
 * Calculate responsive image carousel dimensions
 */
export const calculateCarouselDimensions = (
  layoutMode: LayoutMode,
  screenWidth: number
): { width: number; height: number } => {
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  
  // Mobile: full width minus padding and frame
  if (isMobile) {
    const framePadding = (3 + 2 + 1) * 2;
    const contentPadding = 40; // 20px on each side
    const width = screenWidth - framePadding - contentPadding;
    return { width, height: width * 0.9 };
  }
  
  // Tablet: constrained width
  if (isTablet) {
    const maxWidth = Math.min(screenWidth - 64, 500);
    return { width: maxWidth, height: maxWidth * 0.85 };
  }
  
  // Desktop: fixed max size
  const maxWidth = Math.min(screenWidth - 80, 560);
  return { width: maxWidth, height: maxWidth * 0.8 };
};

